'use client';

import { memo } from 'react';
import { VStack, Box, token } from '@terra/ui-styled-system';
import { Text, Tooltip, TechnologyIcon } from '@terra/ui-design-system';
import { useTranslations } from 'next-intl';
import { Handle, Position } from '@xyflow/react';
import { formatCloudProviderLabel } from '@terra/shared-contracts-integrations';
import {
  GRAPH_NODE_CIRCLE_SIZE,
  GRAPH_NODE_ICON,
  GRAPH_NODE_COLOR,
  type AssetNodeData,
} from './constants';
import {
  ExternalNetworkGraphEdge,
  externalNetworkEdgeTypes,
} from './external-network-graph-edge';
import { useExternalNetworkGraphNodeActions } from './ExternalNetworkGraphNodeActionsContext';
import { ExternalNetworkGraphNodePopover } from './ExternalNetworkGraphNodePopover';
import { ExternalNetworkGraphNodeToolbar } from './ExternalNetworkGraphNodeToolbar';

const SELECTED_NODE_RING_SHADOW = `0 0 0 6px ${token('colors.border.accent.focus')}`;

type ExternalNetworkGraphNodeProps = {
  id: string;
  data: AssetNodeData;
  selected?: boolean;
};

export const ExternalNetworkGraphNode = memo(
  ({ id, data, selected }: ExternalNetworkGraphNodeProps) => {
    const t = useTranslations('externalNetwork');
    const nodeActions = useExternalNetworkGraphNodeActions();
    const Icon = GRAPH_NODE_ICON[data.entityKind] ?? GRAPH_NODE_ICON.DOMAIN;
    const color = GRAPH_NODE_COLOR[data.entityKind] ?? '#6b7280';
    const isCluster = data.nodeType === 'cluster';
    const isSyntheticKind =
      data.entityKind === 'INTERNET' ||
      data.entityKind === 'CONNECTOR' ||
      data.entityKind === 'CLOUD_ACCOUNT' ||
      data.entityKind === 'APPLICATION' ||
      data.entityKind === 'WEAKNESS';
    const hideEntityKindLabel =
      data.entityKind === 'INTERNET' ||
      data.entityKind === 'CLOUD_ACCOUNT' ||
      data.entityKind === 'WEAKNESS';
    const entityKindLabel = data.entityKind.replace(/_/g, ' ').toLowerCase();
    const ringShadows = [selected ? SELECTED_NODE_RING_SHADOW : null].filter(
      Boolean,
    );
    const showToolbar =
      Boolean(data.isActionTarget) && !isCluster && !isSyntheticKind;

    const nodeContent = (
      <Box position="relative">
        <VStack
          gap={0}
          alignItems="center"
          transition="transform 0.15s ease"
          _hover={{ transform: 'scale(1.05)' }}
          cursor="pointer"
        >
          <Box position="relative">
            <Handle
              type="target"
              position={Position.Left}
              style={{ opacity: 0, top: GRAPH_NODE_CIRCLE_SIZE / 2 }}
            />
            <Box
              w={`${GRAPH_NODE_CIRCLE_SIZE}px`}
              h={`${GRAPH_NODE_CIRCLE_SIZE}px`}
              borderRadius="full"
              display="flex"
              alignItems="center"
              justifyContent="center"
              style={{
                backgroundColor:
                  data.entityKind === 'CLOUD_ACCOUNT'
                    ? token('colors.bg.muted')
                    : color,
                border:
                  data.entityKind === 'CLOUD_ACCOUNT'
                    ? `1px solid ${token('colors.border.subtle')}`
                    : isCluster
                      ? '2px dashed rgba(255,255,255,0.8)'
                      : undefined,
                ...(ringShadows.length > 0
                  ? { boxShadow: ringShadows.join(', ') }
                  : {}),
              }}
              mb={3}
            >
              {data.entityKind === 'CLOUD_ACCOUNT' && data.cloudProvider ? (
                <TechnologyIcon
                  name={data.cloudProvider}
                  size={22}
                  showInitials
                />
              ) : (
                <Icon size={18} color="white" strokeWidth={2} />
              )}
            </Box>
            <Handle
              type="source"
              position={Position.Right}
              style={{ opacity: 0, top: GRAPH_NODE_CIRCLE_SIZE / 2 }}
            />
            {data.cveCount != null && data.cveCount > 0 && (
              <Tooltip
                title={t('graph.tooltip.cvesBadge', { count: data.cveCount })}
                positioning={{ strategy: 'fixed', placement: 'top' }}
              >
                <Box
                  position="absolute"
                  top="-4px"
                  right="-4px"
                  w="18px"
                  h="18px"
                  borderRadius="full"
                  bg="red.500"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Text
                    fontSize="9px"
                    color="white"
                    fontWeight="bold"
                    lineHeight="1"
                  >
                    {data.cveCount}
                  </Text>
                </Box>
              </Tooltip>
            )}
          </Box>

          <Box mb={1.5}>
            <Text fontSize="sm" fontWeight={500} color="fg.default">
              {data.label}
            </Text>
          </Box>

          <Box
            mb={1.5}
            visibility={hideEntityKindLabel ? 'hidden' : 'visible'}
            aria-hidden={hideEntityKindLabel}
          >
            <Text fontSize="sm" color="fg.muted">
              {data.entityKind === 'APPLICATION' && data.isDraft
                ? t('webApp.draftBadge')
                : isCluster
                  ? t('graph.clusterEntityKind', {
                      entityKind: entityKindLabel,
                    })
                  : entityKindLabel}
            </Text>
          </Box>

          {data.entityKind === 'NETWORK_SERVICE' && data.transportProtocol && (
            <Text fontSize="xs" color="fg.subtle">
              {data.transportProtocol}
            </Text>
          )}

          {data.cloudProvider && data.entityKind !== 'CLOUD_ACCOUNT' && (
            <Text fontSize="xs" color="fg.subtle">
              {formatCloudProviderLabel({ provider: data.cloudProvider })}
            </Text>
          )}
        </VStack>
      </Box>
    );

    return (
      <Box position="relative" opacity={1}>
        {showToolbar && nodeActions ? (
          <ExternalNetworkGraphNodeToolbar nodeId={id}>
            <ExternalNetworkGraphNodePopover
              node={{ id, kind: data.entityKind, label: data.label }}
              isExpanding={Boolean(data.isExpanding)}
              expansion={data.expansion}
              onExpand={() => {
                nodeActions.onCloseSelection();
                nodeActions.onExpand(id);
              }}
              onViewDetails={() => nodeActions.onViewDetails(id)}
              onClose={() => nodeActions.onCloseSelection()}
            />
          </ExternalNetworkGraphNodeToolbar>
        ) : null}
        {nodeContent}
      </Box>
    );
  },
);
ExternalNetworkGraphNode.displayName = 'ExternalNetworkGraphNode';

export { ExternalNetworkGraphEdge, externalNetworkEdgeTypes };

export const externalNetworkNodeTypes = {
  externalNetworkNode: ExternalNetworkGraphNode,
};
